import { useEffect, useRef, useState } from 'react';
import { typeOfMake } from '../lib/boxTypes.ts';
import { MakeTag } from '../lib/jobMakes.tsx';
import { api, fmt, fmtDate, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import NewOrderForm from '../components/NewOrderForm.tsx';
import { useList } from '../lib/useList.ts';

/** One line of an order. The line, not the order, is the unit of work. */
interface LineRow {
  po_item_id: number;
  po_id: number;
  po_number: string | null;
  so_number: number;
  sales_order: string;
  line_no: number;
  line_ref: string;
  po_date: string;
  priority: string;
  customer_name: string;
  box_name: string;
  job_numbers: string | null;
  box_size: string | null;
  box_type: string | null;
  make: string | null;
  job_work: boolean;
  customer_material: boolean;
  ups: number | null;
  quantity: string;
  unit_price: string | null;
  line_value: string;
  delivery_date: string | null;
  delivery_state: string;
  line_status: 'OPEN' | 'PART_DISPATCHED' | 'DISPATCHED' | 'CANCELLED';
}

interface LineJob {
  id: number; job_number: string; ordered_quantity: string; place: string;
  delivery_date: string | null; own_delivery_date: boolean;
}

const STATUS_LABEL: Record<LineRow['line_status'], string> = {
  OPEN: 'Open', PART_DISPATCHED: 'Part dispatched', DISPATCHED: 'Dispatched', CANCELLED: 'Cancelled',
};

type Tab = 'open' | 'late' | 'dispatched' | 'all';

/**
 * What each tab asks the list for. Late is the view's own delivery_state, which
 * is CLOSED for dispatched and cancelled lines, so it never shows finished work.
 */
const TAB_FILTERS: Record<Tab, Record<string, string>> = {
  open: { line_status: 'OPEN,PART_DISPATCHED' },
  late: { delivery_state: 'LATE' },
  dispatched: { line_status: 'DISPATCHED' },
  all: {},
};
/** The filters a tab owns, as opposed to ones the person set on a column. */
const VIEW_KEYS = ['line_status', 'delivery_state'];

interface Counts { open: number; late: number; dispatched: number; all: number }

/** 12 X 8 X 22 as read in the sheet becomes 12 × 8 × 22. */
const sizeText = (s: string) => s.replace(/\s*[xX×]\s*/g, ' × ');

export default function Orders({
  user, onOpenOrder, onOpenJob,
}: {
  user: User;
  onOpenOrder: (poId: number, poItemId?: number) => void;
  onOpenJob: (jobId: number) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [jobs, setJobs] = useState<Map<number, LineJob[] | 'error'>>(new Map());
  const [notice, setNotice] = useState('');
  const list = useList<LineRow>('/api/purchase-orders', 'purchaseOrders',
                                { sort: 'po_date', dir: 'desc', filters: TAB_FILTERS.open });
  const [tab, setTab] = useState<Tab>('open');
  const [counts, setCounts] = useState<Counts | null>(null);
  // The tab a search took you away from, so clearing the search brings you back.
  const before = useRef<Tab | null>(null);

  function pickTab(t: Tab) {
    const rest = Object.fromEntries(
      Object.entries(list.state.filters).filter(([k]) => !VIEW_KEYS.includes(k)));
    setTab(t);
    list.set({ filters: { ...rest, ...TAB_FILTERS[t] } });
  }

  // Looking for something means looking everywhere: a finished order must be
  // findable by typing its company or box, whichever tab you were on.
  const searching = list.state.search.trim() !== '';
  useEffect(() => {
    if (searching && tab !== 'all') {
      before.current = tab;
      pickTab('all');
    } else if (!searching && before.current) {
      const back = before.current;
      before.current = null;
      pickTab(back);
    }
    // Only a change in whether a search is typed should move the tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching]);

  // The tab numbers come from the same view the list reads. Refetched with
  // every load, so an order created or dispatched shows up in them.
  useEffect(() => {
    api.get<{ counts: Counts }>('/api/purchase-orders/counts')
      .then((r) => setCounts(r.counts))
      .catch(() => {});
  }, [list.rows]);

  const canCreate = user.role === 'ADMIN' || user.role === 'MANAGER';

  if (creating) {
    return (
      <NewOrderForm
        onCancel={() => setCreating(false)}
        onCreated={(msg) => {
          setCreating(false); setNotice(msg); void list.reload();
        }}
      />
    );
  }

  function toggle(id: number) {
    setOpen((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    api.get<{ jobs: LineJob[] }>(`/api/po-items/${id}/jobs`)
      .then((r) => setJobs((m) => new Map(m).set(id, r.jobs)))
      .catch(() => setJobs((m) => new Map(m).set(id, 'error')));
  }

  const columns: Column<LineRow>[] = [
    {
      key: 'expand', label: '', sortable: false, filterable: false, width: '2.75rem',
      render: (r) => (
        <button className="expander" aria-expanded={open.has(r.po_item_id)}
                aria-label={`${open.has(r.po_item_id) ? 'Hide' : 'Show'} jobs for ${r.line_ref}`}
                onClick={(e) => { e.stopPropagation(); toggle(r.po_item_id); }}>
          {open.has(r.po_item_id) ? '▾' : '▸'}
        </button>
      ),
    },
    {
      key: 'so_number', label: 'Order', type: 'number', width: '10%',
      render: (r) => (
        <button className="linkish mono" title="Open the sales order"
                onClick={(e) => { e.stopPropagation(); onOpenOrder(r.po_id, r.po_item_id); }}>
          {r.line_ref}
        </button>
      ),
    },
    { key: 'customer_name', label: 'Company', width: '22%' },
    {
      key: 'box_name', label: 'Box', width: '22%',
      render: (r) => (
        <>
          <div>{r.box_name}</div>
          <div className="dim order-detail">
            {[r.box_size && sizeText(r.box_size),
              r.line_status !== 'OPEN' && STATUS_LABEL[r.line_status]].filter(Boolean).join(' · ') || '—'}
          </div>
        </>
      ),
    },
    {
      // The same names as the Make choice on the order form. No Make saved is
      // the ordinary Printed Carton Box.
      key: 'make', label: 'Make', filterable: false, width: '17%',
      // The ordinary box is the common case, so it is quiet; the four special
      // makes read in full ink and stand out down the column.
      render: (r) => (
        <>
          <div>{r.make ? typeOfMake(r.make).label : <span className="dim">{typeOfMake(r.make).label}</span>}</div>
          {(r.job_work || r.customer_material) && (
            <div className="dim order-detail">
              {[r.job_work && 'Job work', r.customer_material && 'Party\'s board'].filter(Boolean).join(' · ')}
            </div>
          )}
        </>
      ),
    },
    { key: 'quantity', label: 'Quantity', type: 'number', align: 'right', width: '10%',
      render: (r) => fmt(r.quantity) },
    {
      key: 'delivery_date', label: 'Delivery', type: 'date',
      render: (r) => (
        <>
          {r.delivery_date
            ? <span className={r.delivery_state === 'LATE' ? 'late-text' : undefined}>{fmtDate(r.delivery_date)}</span>
            : <span className="dim">No date</span>}
          {(r.priority === 'URGENT' || r.priority === 'HIGH') && r.line_status !== 'DISPATCHED'
            && r.line_status !== 'CANCELLED' && (
            <span className={`tag ${r.priority === 'URGENT' ? 'urgent' : 'high'}`} style={{ marginLeft: 8 }}>
              {r.priority === 'URGENT' ? 'Urgent' : 'High'}
            </span>
          )}
        </>
      ),
    },
  ];

  const EMPTY: Record<Tab, string> = {
    open: 'No open orders.', late: 'Nothing is late.', dispatched: 'Nothing dispatched yet.',
    all: 'No orders yet.',
  };

  return (
    <div className="panel">
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}
      <DataTable
        list={list}
        columns={columns}
        exportName="/api/purchase-orders"
        moreMenu
        tableClass="ord-table"
        viewFilters={VIEW_KEYS}
        tabs={[
          { key: 'open', label: 'Open', count: counts?.open },
          { key: 'late', label: 'Late', count: counts?.late },
          { key: 'dispatched', label: 'Dispatched' },
          { key: 'all', label: 'All' },
        ]}
        activeTab={tab}
        onTab={(k) => { before.current = null; pickTab(k as Tab); }}
        rowKey={(r) => String(r.po_item_id)}
        rowClass={() => 'ord-row'}
        onRowClick={(r) => onOpenOrder(r.po_id, r.po_item_id)}
        below={(r) => {
          if (!open.has(r.po_item_id)) return null;
          const list = jobs.get(r.po_item_id);
          const cls = (c: Column<LineRow>) => [c.align === 'right' ? 'num' : '', c.secondary ? 'col-secondary' : '']
            .filter(Boolean).join(' ');
          if (list === undefined || list === 'error' || list.length === 0) {
            return (
              <tr className="sub-row">
                <td />
                <td colSpan={columns.length - 1} className="dim">
                  {list === undefined ? 'Loading…' : list === 'error' ? 'Could not load the jobs' : 'No job card yet'}
                </td>
              </tr>
            );
          }
          return list.map((j) => (
            <tr key={j.id} className="sub-row">
              {columns.map((c) => (
                <td key={c.key} className={cls(c)}>
                  {c.key === 'so_number' ? (
                    <>
                      <button className="linkish mono sub-job" title="Open the job card"
                              onClick={() => onOpenJob(j.id)}>
                        {j.job_number}
                      </button>
                      <MakeTag number={j.job_number} />
                    </>
                  ) : c.key === 'quantity' ? fmt(j.ordered_quantity)
                    : c.key === 'delivery_date' ? (
                      j.own_delivery_date && <span className="dim">{fmtDate(j.delivery_date)} · own</span>
                    ) : c.key === 'box_name' ? <span className="dim">{j.place}</span> : null}
                </td>
              ))}
            </tr>
          ));
        }}
        empty={`${EMPTY[tab]}${tab === 'all' && canCreate ? ' Use “New sales order” to add the first one.' : ''}`}
        toolbar={canCreate && (
          <button className="btn primary" onClick={() => { setNotice(''); setCreating(true); }}>
            New sales order
          </button>
        )}
      />
    </div>
  );
}
